import { request } from '../utils';

// Reusable API module wrapping the six maker-checker endpoints and normalising
// the standard envelope `{ request_id, status, message, data, meta }`.
//
// On success each method resolves with the full envelope (use `.data` for the
// resource and `.meta` for extra info). On failure it rejects with the server's
// error envelope `{ status, message, errors, ... }` so callers can branch on
// `status`/`message` and pass it straight to `showError`.

const unwrap = (res) => res.data;

const fail = (err) => {
  if (err.response) {
    return Promise.reject(err.response.data);
  }
  // Navigation abort (or another interceptor's already-shaped rejection) —
  // pass it through so showError can recognise it instead of relabelling it a
  // network error.
  if (err && !err.isAxiosError) {
    return Promise.reject(err);
  }
  return Promise.reject({
    message: err.message || 'Network Error. Please try again.',
    title: '❌ Network Error',
  });
};

export default {
  // POST /admin/approval-requests — create a pending request from a captureToken.
  // `resourceUri` is an optional maker-supplied review link (relative path or
  // http(s) URL); the server saves unsafe values as null.
  createRequest(captureToken, resourceUri) {
    const body = { captureToken };
    if (resourceUri) body.resourceUri = resourceUri;
    return request.post('/approval-requests', body).then(unwrap, fail);
  },

  // GET /admin/approval-requests — keyset-paginated list, newest first.
  //
  // Filtering travels entirely in `where`: a sqlize-query criteria object that
  // is JSON-stringified into a single param. The old ?status / ?action / ?mine
  // params are gone — express them as where clauses instead. Paging is
  // forward-only: omit `cursor` for page one, then echo `meta.next_cursor`
  // until `meta.has_more` is false.
  //
  // Resolves with `{ data, meta: { limit, has_more, next_cursor } }`.
  //
  // @param {Object} where  — sqlize-query criteria; omitted when empty
  // @param {number} limit  — page size (server clamps to 1–100, defaults to 30)
  // @param {string} cursor — opaque; pass through untouched
  // @param {boolean} abortOnNavigate — false for callers outside the page tree
  //        (the sidebar badge), whose polling must survive navigation
  list({ where, limit, cursor, abortOnNavigate } = {}) {
    const params = {};
    if (where && Object.keys(where).length > 0) {
      params.where = JSON.stringify(where);
    }
    if (limit != null) params.limit = limit;
    if (cursor != null) params.cursor = cursor;
    return request
      .get('/approval-requests', {
        params,
        ...(abortOnNavigate !== undefined && { abortOnNavigate }),
      })
      .then(unwrap, fail);
  },

  // GET /admin/approval-request-actions — the action vocabulary as
  // [{ action, label }], already sorted by label. Takes no params, is not
  // paginated, and grows as more operations are put behind maker-checker, so
  // callers must fetch it rather than hardcoding options or deriving labels.
  listActions() {
    return request.get('/approval-request-actions').then(unwrap, fail);
  },

  // GET /admin/approval-requests/:reference — detailed request
  get(reference) {
    return request.get(`/approval-requests/${reference}`).then(unwrap, fail);
  },

  // POST /admin/approval-requests/:reference/otp — send OTP to the checker
  sendOtp(reference) {
    return request
      .post(`/approval-requests/${reference}/otp`)
      .then(unwrap, fail);
  },

  // POST /admin/approval-requests/:reference/approval — approve (runs the action)
  approve(reference, otp) {
    const body = otp ? { otp } : {};
    return request
      .post(`/approval-requests/${reference}/approval`, body)
      .then(unwrap, fail);
  },

  // DELETE /admin/approval-requests/:reference/approval — decline with a reason.
  // axios sends a request body for DELETE via the `data` config key; the reason
  // is also passed as a query param for backends that read `?reason=`.
  decline(reference, reason) {
    return request
      .delete(`/approval-requests/${reference}/approval`, {
        data: { reason },
        params: { reason },
      })
      .then(unwrap, fail);
  },
};
